/* Tagalys Headless usage documentation:
 ** - https://tagalys.notion.site/Search-results-666b0999526a4ac490de4c2664bfd165#43e375c82e5b46e7bce3573067a1aabc
 */

/* Recommended extension
 ** - https://marketplace.visualstudio.com/items?itemName=Tobermory.es6-string-html
 */

/* global Tagalys */

const { initYotpoWidgets, translateString, formatMoney } = window.BAO.Utils
const { ProductCard } = window.BAO.Tagalys
const translations = window.theme.strings

class TagalysSearch extends window.BAO.CustomElement() {
  static get requiredElements () {
    return ['form']
  }

  connectedCallback () {
    super.connectedCallback()

    this.activeFilterId = ''
    this.selectedRangeValues = {}
    this.currentProductCount = 0

    if (!window?.onTagalysReady) return

    // eslint-disable-next-line no-undef
    onTagalysReady(() => {
      this.dispatchEvent(new CustomEvent('tagalys:ready'))

      // eslint-disable-next-line no-undef
      Tagalys.APIConnector.setQueryStringConfiguration({
        queryParameter: 'q',
        queryFilterParameter: 'qf',
        filterParameter: 'f',
        pageParameter: 'page',
        sortParameter: 'sort',
      })

      // eslint-disable-next-line no-undef
      this.searchResultsInstance = Tagalys.APIConnector.SearchResults.new({
        params: {
          query: this.searchTerm,
          perPage: 24,
        },
        onSuccess: this.handleSuccess.bind(this),
        onFailure: this.handleFailure.bind(this),
      })

      // Perform the API call and trigger the onSuccess callback
      this.searchResultsInstance.helpers.call()
    })
  }

  setupListeners () {
    super.setupListeners()

    this.listeners.add(this, 'click', this.handleClick.bind(this))

    this.formEls.forEach((formEl) =>
      this.listeners.add(formEl, 'change', this.handleChange.bind(this)),
    )

    this.listeners.add(
      this,
      'filter-facet:close',
      this.handleFilterFacetClose.bind(this),
    )
  }

  handleSuccess (response, helpers) {
    this.helpers = helpers

    this.log('Success')

    if (this.helpers?.isRedirected()) {
      this.redirect(this.helpers?.getRedirectURL())
      return
    }

    this.dispatchEvent(
      new CustomEvent('tagalys:success', { detail: { helpers } }),
    )

    this.updateUrl()
  }

  handleFailure (response) {
    this.log('Failure')

    this.dispatchEvent(new CustomEvent('tagalys:failure'))
  }

  redirect (url) {
    if (!url) return

    window.location.replace(url)
  }

  updateUrl () {
    const requestParameters = this.helpers.getRequestParams()

    window.history.replaceState(
      requestParameters,
      '',
      `${translations.search_url}?${this.helpers.getEncodedQueryString(
        requestParameters,
      )}`,
    )
  }

  handleClick (event) {
    const clearFilterButtonEl = event.target.closest('[data-clear-filter]')

    if (clearFilterButtonEl) {
      this.clearFilter(
        clearFilterButtonEl.dataset?.filterId,
        clearFilterButtonEl.dataset?.filterValue,
        clearFilterButtonEl.hasAttribute('data-make-active'),
      )
    }

    const clearAllButtonEl = event.target.closest('[data-clear-all]')

    if (clearAllButtonEl) {
      this.helpers.clearAllFilters()
    }
  }

  handleChange (event) {
    const targetEl = event?.target

    if (!targetEl) return

    if (targetEl.matches('[data-apply-filter]')) {
      this.applyFilter(
        targetEl.dataset?.filterId,
        targetEl.dataset?.filterValue,
        targetEl.hasAttribute('data-make-active'),
      )
    }

    if (targetEl.matches('[data-apply-range-filter]')) {
      this.applyRangeFilter(targetEl.dataset?.filterId, targetEl)
    }

    if (targetEl.matches('[data-clear-filter]')) {
      this.clearFilter(
        targetEl.dataset?.filterId,
        targetEl.dataset?.filterValue,
        targetEl.hasAttribute('data-make-active'),
      )
    }

    if (targetEl.matches('[data-apply-sort-option]')) {
      this.applySortOption(targetEl.value)
    }
  }

  handleFilterFacetClose () {
    /* Unset the active filter id because the user has closed the facet */
    this.activeFilterId = null
  }

  applyRangeFilter (filterId, filterEl) {
    if (!this.helpers || !filterId) return
    if (Number(filterEl.value) < Number(filterEl.min)) return
    if (Number(filterEl.value) > Number(filterEl.max)) return

    const selectedValues = {}
    const currentSelectedMin = this.selectedRangeValues[filterId]?.selected_min
    const currentSelectedMax = this.selectedRangeValues[filterId]?.selected_max

    if (filterEl.hasAttribute('data-range-min')) {
      selectedValues.selected_min = filterEl.value
      selectedValues.selected_max = currentSelectedMax?.length
        ? currentSelectedMax
        : filterEl.getAttribute('max')

      this.selectedRangeValues[filterId].selected_min = filterEl.value
    }

    if (filterEl.hasAttribute('data-range-max')) {
      selectedValues.selected_min = currentSelectedMin?.length
        ? currentSelectedMin
        : filterEl.getAttribute('min')
      selectedValues.selected_max = filterEl.value

      this.selectedRangeValues[filterId].selected_max = filterEl.value
    }

    this.applyFilter(
      filterId,
      selectedValues,
      filterEl.hasAttribute('data-make-active'),
    )
  }

  applyFilter (filterId, filterValue, isActive) {
    if (!this.helpers || !filterId) return
    this.activeFilterId = isActive ? filterId : null

    this.helpers.applyFilter(filterId, filterValue)
  }

  clearFilter (filterId, filterValue, isActive) {
    if (!this.helpers || !filterId) return
    this.activeFilterId = isActive ? filterId : null

    if (this.selectedRangeValues?.[filterId]) {
      this.selectedRangeValues[filterId] = {}
    }

    this.helpers.clearFilter(filterId, filterValue)
  }

  applySortOption (sortValue) {
    if (!this.helpers || !sortValue) return
    this.activeFilterId = null

    this.helpers.applySortOption(sortValue)
  }

  get formEls () {
    return this.els.form?.elements
  }

  get searchTerm () {
    return new URLSearchParams(window.location.search)?.get('q') || ''
  }
}

class TagalysSearchChildComponent extends window.BAO.CustomElement() {
  setupListeners () {
    super.setupListeners()

    this.listeners.add(
      this.tagalysSearch,
      'tagalys:success',
      this.render.bind(this),
    )
  }

  get tagalysSearch () {
    return this.closest('tagalys-search')
  }
}

class TagalysResultsText extends TagalysSearchChildComponent {
  render (event) {
    this.helpers = event?.detail?.helpers

    if (!this.helpers?.getQuery()?.length) return

    this.resultsTextHTML = translateString(
      translations.search_results_template.showing_results,
      {
        count: this.helpers.getTotalProductsCount(),
        term: this.tagalysSearch.searchTerm,
      },
    )

    if (this.helpers?.isSpellingCorrectedQuery()) {
      this.resultsTextHTML = translateString(
        translations.search_results_template.spelling_corrected,
        {
          original_query: this.helpers.getOriginalQuery(),
          query: this.helpers.getQuery(),
        },
      )
    }

    if (this.helpers?.isPartialResults()) {
      this.resultsTextHTML = translateString(
        translations.search_results_template.partial_match,
        {
          query: this.helpers.getQuery(),
        },
      )
    }

    console.log(this.helpers?.hasNoSearchResults())

    if (this.helpers?.hasNoSearchResults()) {
      const widgetId = window.BAO.Tagalys.smartWidgetId
    
      Tagalys.APIConnector.SmartWidget.call({
        params: {
          widgetId,
          limit: 16,
        },
        onSuccess: async (response) => {
          const products = response?.products
    
          const items = await Promise.all(
            products.map(async (product) => {
              this.tagalysSearch.currentProductCount += 1
              const productHTML = await this.productCardHTML(product)
              return `<li class="prd-List_Item">${productHTML}</li>`
            })
          )
    
          const newProductListHtml = items.join('')
          const targetDiv = document.querySelector(
            '[data-tagalys-product-list-el="target"]',
          )
    
          if (targetDiv) targetDiv.innerHTML = newProductListHtml
    
          // Update UI message to reflect fallback content
          this.innerHTML = `<div class="fallback-results-heading">No Results Found</div>`
        },
        onFailure: (response) => {
          console.error('API call failed:', response)
    
          this.innerHTML = translateString(
            translations.search_results_template.no_results,
            {
              original_query: this.helpers.getOriginalQuery(),
              query: this.helpers.getQuery(),
            },
          )
        },
      })
    } else {
      this.innerHTML = this.resultsTextHTML
    }
  }

  async productCardHTML (product) {
    const productCard = new ProductCard(product, { lazyload: false })
    return await productCard.render()
  }
}

class TagalysProductList extends TagalysSearchChildComponent {
  static get requiredElements () {
    return ['target']
  }

  connectedCallback () {
    super.connectedCallback()

    this.productListHTML = ''
  }

  async render (event) {
    this.helpers = event?.detail?.helpers
    let products = this.helpers.getProducts()
  
    if (!this.els.target?.element) return
  
    // Handle edge case: promises or bad data inside products array
    if (products?.some(p => typeof p?.then === 'function')) {
      console.warn('Products contain unresolved Promises:', products)
      products = await Promise.all(products)
    }
  
    if (products.length) {
      await this.renderResults(products)
    }
  
    initYotpoWidgets()
    this.updateOmetriaTracking(
      this.searchTerm,
      this.helpers.getTotalProductsCount(),
    )
  }

  async renderResults (products) {
    this.tagalysSearch.currentProductCount = this.tagalysSearch?.isLoadingMore
      ? this.tagalysSearch.currentProductCount
      : 0

    const newProductListHtml = await Promise.all(
      products.map(async (product) => {
        this.tagalysSearch.currentProductCount =
          this.tagalysSearch.currentProductCount + 1

        return `
        <li class="prd-List_Item">
          ${await this.productCardHTML(product)}
        </li>`
      }),
    )

    if (this.tagalysSearch?.isLoadingMore) {
      this.tagalysSearch.isLoadingMore = false

      this.productListHTML = this.productListHTML + newProductListHtml.join('')
    } else {
      this.productListHTML = newProductListHtml.join('')
    }

    this.els.target.element.innerHTML = this.productListHTML
  }

  async productCardHTML (product) {
    return await new ProductCard(product, { lazyload: false }).render()
  }

  updateOmetriaTracking (searchTerm, productCount) {
    // Updates the Ometria tracking pixel
    // Affects snippets/ometria-tracking-pixel.liquid
    if (typeof ometria === 'undefined') window.ometria = {}

    Object.assign(window.ometria.raw_data, {
      search_count: productCount,
      search_terms: searchTerm,
    })
  }

  get searchTerm () {
    return new URLSearchParams(window.location.search)?.get('q') || ''
  }
}

class TagalysPagination extends TagalysSearchChildComponent {
  static get requiredElements () {
    return ['container', 'load-button', 'count']
  }

  static get requiredAttributes () {
    return ['data-direction']
  }

  connectedCallback () {
    super.connectedCallback()

    this.canSetLoadMoreVisibility = true
    this.canSetLoadPreviousVisibility = true
  }

  setupListeners () {
    super.setupListeners()

    this.listeners.add(this, 'click', this.handleClick.bind(this))
  }

  render (event) {
    this.helpers = event?.detail?.helpers

    const totalProductCount = this.helpers.getTotalProductsCount()

    if (!totalProductCount) return

    this.setVisibility()
    this.setLoadMoreButtonState()

    this.renderPaginationCount(
      this.tagalysSearch.currentProductCount,
      totalProductCount,
    )
  }

  handleClick (event) {
    const loadButtonEl = event.target.closest(
      this.els['load-button']?.selector,
    )
    if (!loadButtonEl) return

    if (this.isLoadMore) {
      this.tagalysSearch.isLoadingMore = true

      this.helpers?.goToNextPage()
    }

    if (this.isLoadPrevious) {
      this.helpers?.goToPreviousPage()
    }
  }

  setVisibility () {
    if (this.isLoadMore && this.canSetLoadMoreVisibility) {
      this.containerEl.setAttribute(
        'aria-hidden',
        !this.helpers?.hasNextPage(),
      )

      this.canSetLoadMoreVisibility = false
    }

    if (this.isLoadPrevious) {
      if (
        this.canSetLoadPreviousVisibility ||
        this.helpers.getCurrentPage() === 1
      ) {
        this.containerEl.setAttribute(
          'aria-hidden',
          !this.helpers?.hasPreviousPage(),
        )

        this.canSetLoadPreviousVisibility = false
      }
    }
  }

  setLoadMoreButtonState () {
    if (this.isLoadPrevious) return
    this.loadButtonEl.toggleAttribute('disabled', !this.helpers?.hasNextPage())

    // Update button text
    if (this.helpers?.hasNextPage() === true) {
      this.loadMoreTextEl.removeAttribute('hidden')
      this.showingAllTextEl.setAttribute('hidden', true)
    } else {
      this.showingAllTextEl.removeAttribute('hidden')
      this.loadMoreTextEl.setAttribute('hidden', true)
    }
  }

  renderPaginationCount (productCount, totalProductCount) {
    this.countEls.forEach((countEl) => {
      countEl.innerHTML = translateString(translations.pagination.count, {
        current: productCount,
        total: totalProductCount,
        items: translations.pagination.products,
      })
    })
  }

  get isLoadMore () {
    return this.dataset.direction === 'next'
  }

  get isLoadPrevious () {
    return this.dataset.direction === 'previous'
  }

  get containerEl () {
    return this.els.container?.element
  }

  get loadButtonEl () {
    return this.els['load-button']?.element
  }

  get loadMoreTextEl () {
    return this.els['load-more-text']?.element
  }

  get showingAllTextEl () {
    return this.els['showing-all-text']?.element
  }

  get countEls () {
    return this.els.count?.elements
  }
}

class TagalysFacets extends TagalysSearchChildComponent {
  static get requiredElements () {
    return ['target']
  }

  static get requiredAttributes () {
    return ['data-class-prefix']
  }

  render (event) {
    this.helpers = event?.detail?.helpers

    const filters = this.helpers.getFilters()

    if (!filters?.length) return

    let facetsHTML = ''

    filters.forEach((filter) => {
      switch (filter?.type) {
        case 'checkbox':
          facetsHTML += this.checkboxFilterHTML(filter)
          break
        case 'range':
          facetsHTML += this.rangeFilterHTML(filter)
          break
        default:
          this.log({ 'Filter type': filter?.type })
      }
    })

    this.targetEl.innerHTML = facetsHTML
  }

  checkboxFilterHTML (filter) {
    return /* html */ `
      <div class="${this.classPrefix}_Item">
        <details class="src-ProductGridFacet"
          is="filter-facet"
          data-index="Filter${filter.id}"
          aria-current="${
            this.tagalysSearch.activeFilterId === filter.id ? 'true' : 'false'
          }"
          ${this.tagalysSearch.activeFilterId === filter.id ? 'open' : ''}
        >
          <summary class="src-ProductGridFacet_Summary" data-filter-facet-el="summary">
            <span class="src-ProductGridFacet_FilterLabel">
              ${filter.name}
            </span>

            ${this.iconChevronHTML}
          </summary>

          <div class="src-ProductGridFacet_Display" data-filter-facet-el="content">
            <div class="src-ProductGridFacet_Body">
              <ul class="src-ProductGridFacet_List">
                ${filter.items
                  .map((item) => {
                    return /* html */ `
                    <li class="src-ProductGridFacet_Item">
                      <label class="src-ProductGridFacet_Label fz-14_140" for="${
                        this.classPrefix
                      }_Filter-${item.id}">
                        <input class="src-ProductGridFacet_Checkbox"
                          type="checkbox"
                          name="${item.id}"
                          value="1"
                          id="${this.classPrefix}_Filter-${item.id}"
                          ${item.selected ? 'checked' : ''}
                          ${
                            item.selected
                              ? 'data-clear-filter'
                              : 'data-apply-filter'
                          }
                          data-filter-id="${filter.id}"
                          data-filter-value="${item.id}"
                          data-make-active
                        >

                        <span class="src-ProductGridFacet_CheckboxIcon"></span>

                        ${item.name}

                        <span class="src-ProductGridFacet_Count">${
                          item.count
                        }</span>
                      </label>
                    </li>
                  `
                  })
                  .filter(Boolean)
                  .join('')}
              </ul>
            </div>

            <footer class="src-ProductGridFacet_Footer">
              <div class="src-ProductGridFacet_Remove">
                <button class="src-ProductGridFacet_RemoveLink"
                  type="button"
                  data-clear-filter
                  data-filter-id="${filter.id}"
                  data-make-active
                >
                  ${translations.search_results_template.facets.clear_all}
                </button>
              </div>
            </footer>
          </div>
        </details>
      </div>
    `
  }

  rangeFilterHTML (filter) {
    this.tagalysSearch.selectedRangeValues[filter.id] =
      this.tagalysSearch.selectedRangeValues?.[filter.id] || {}

    const currentSelectedMin =
      this.tagalysSearch.selectedRangeValues[filter.id]?.selected_min || ''
    const currentSelectedMax =
      this.tagalysSearch.selectedRangeValues[filter.id]?.selected_max || ''

    return /* html */ `
      <div class="${this.classPrefix}_Item">
        <details class="src-ProductGridFacet src-ProductGridFacet-priceRange"
          is="filter-facet"
          data-index="Filter${filter.id}"
          aria-current="${
            this.tagalysSearch.activeFilterId === filter.id ? 'true' : 'false'
          }"
          ${this.tagalysSearch.activeFilterId === filter.id ? 'open' : ''}
        >
          <summary class="src-ProductGridFacet_Summary" data-filter-facet-el="summary">
            <span class="src-ProductGridFacet_FilterLabel">
              ${filter.name}
            </span>

            ${this.iconChevronHTML}
          </summary>

          <div class="src-ProductGridFacet_Display" data-filter-facet-el="content">
            <header class="src-ProductGridFacet_Header">
              <span>${translateString(
                translations.search_results_template.facets.highest_price,
                { price: formatMoney(filter.max * 100) },
              )}</span>

              <div>
                <button class="src-ProductGridFacet_Remove"
                  type="button"
                  data-clear-filter
                  data-filter-id="${filter.id}"
                  data-make-active
                >
                  ${translations.search_results_template.facets.reset}
                </button>
              </div>
            </header>

            <div class="src-ProductGridFacet_Body">
              <div class="prc-Range">
                <div class="prc-Range_Column">
                  <span class="prc-Range_Symbol">
                    ${translations.currency.symbol}
                  </span>

                  <div class="prc-Range_Field">
                    <input class="prc-Range_Input"
                      name="${filter.id}-gte"
                      id="${this.classPrefix}_Filter-${filter.id}-GTE"
                      type="number"
                      placeholder="${formatMoney(0)}"
                      min="0"
                      max="${
                        currentSelectedMax?.length
                          ? Number(currentSelectedMax) - 1
                          : filter.max
                      }"
                      value="${currentSelectedMin}"
                      data-apply-range-filter
                      data-filter-id="${filter.id}"
                      data-filter-selected-min="${currentSelectedMin}"
                      data-filter-selected-max="${currentSelectedMax}"
                      data-range-min
                      data-make-active
                    >

                    <label class="prc-Range_Label" for="Filter-${
                      filter.id
                    }-GTE">
                      ${translations.search_results_template.facets.from}
                    </label>
                  </div>
                </div>

                <div class="prc-Range_Column">
                  <span class="prc-Range_Symbol">
                    ${translations.currency.symbol}
                  </span>

                  <div class="prc-Range_Field">
                    <input class="prc-Range_Input"
                      name="${filter.id}-lte"
                      id="${this.classPrefix}_Filter-${filter.id}-LTE"
                      type="number"
                      placeholder="${formatMoney(filter.max * 100)}"
                      min="${
                        currentSelectedMin?.length
                          ? Number(currentSelectedMin) + 1
                          : 0
                      }"
                      max="${filter.max}"
                      value="${currentSelectedMax}"
                      data-apply-range-filter
                      data-filter-id="${filter.id}"
                      data-filter-selected-min="${currentSelectedMin}"
                      data-filter-selected-max="${currentSelectedMax}"
                      data-range-max
                      data-make-active
                    >

                    <label class="prc-Range_Label" for="Filter-${
                      filter.id
                    }-LTE">
                      ${translations.search_results_template.facets.to}
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </details>
      </div>
    `
  }

  get iconChevronHTML () {
    return /* html */ `
      <svg aria-hidden="true" focusable="false" role="presentation" viewBox="0 0 10 6" width="10" height="6">
        <path d="M9 0.777832L5 5.22228L1 0.777832" stroke="#23324B" stroke-linecap="round" stroke-linejoin="round" fill="none"></path>
      </svg>
    `
  }

  get classPrefix () {
    return this.dataset.classPrefix
  }

  get targetEl () {
    return this.els.target?.element
  }
}

class TagalysSortSelect extends TagalysSearchChildComponent {
  static get requiredElements () {
    return ['target']
  }

  render (event) {
    this.helpers = event?.detail?.helpers
    const sortOptions = this.helpers?.getSortOptions()

    if (!sortOptions?.length) return

    this.els.target.element.innerHTML = sortOptions
      .map((sortOption) => {
        return /* html */ `
          <option value="${sortOption.id}" ${
          sortOption.selected ? 'selected' : ''
        }>${sortOption.label}</option>
        `
      })
      .filter(Boolean)
      .join('')
  }
}

class TagalysActiveFacets extends TagalysSearchChildComponent {
  static get requiredElements () {
    return ['target']
  }

  render (event) {
    this.helpers = event?.detail?.helpers
    if (!this.helpers || !this.els.target?.element) return

    const activeFilters = this.helpers.hasAnyFiltersApplied()
      ? this.helpers.getAppliedFilters()
      : []

    let activefilterHTML = ''

    activeFilters?.forEach((activeFilter) => {
      switch (activeFilter?.type) {
        case 'checkbox':
          activefilterHTML += this.activeCheckboxFilterHTML(activeFilter)
          break
        case 'range':
          activefilterHTML += this.activeRangeFilterHTML(activeFilter)
          break
        default:
          this.log({ 'Filter type': activeFilter?.type })
      }
    })

    if (activefilterHTML?.length) {
      activefilterHTML += /* html */ `
        <div class="flt-ActiveFacets_Item flt-ActiveFacets_Item-push">
          <button class="flt-ActiveFacets_Link flt-ActiveFacets_Link-destructive"
            type="button"
            data-clear-all
          >
            ${translations.search_results_template.facets.clear_all}
          </button>
        </div>
      `
    }

    this.els.target.element.innerHTML = activefilterHTML
  }

  activeCheckboxFilterHTML (activeFilter) {
    if (!activeFilter?.items?.length) return

    return activeFilter.items
      .map((activeFilterItem) => {
        return /* html */ `
        <div class="flt-ActiveFacets_Item">
          <button class="lb-Label" type="button"
            data-clear-filter
            data-filter-id="${activeFilter.id}"
            data-filter-value="${activeFilterItem.id}"
          >
            ${activeFilterItem.name}

            <span class="lb-Label_Icon"></span>
          </button>
        </div>
      `
      })
      .join('')
  }

  activeRangeFilterHTML (activeFilter) {
    return /* html */ `
    <div class="flt-ActiveFacets_Item">
      <button class="lb-Label" type="button"
        data-clear-filter
        data-filter-id="${activeFilter.id}"
      >
        ${formatMoney(
          activeFilter.selected_min * 100,
        )}&nbsp;-&nbsp;${formatMoney(activeFilter.selected_max * 100)}

        <span class="lb-Label_Icon"></span>
      </button>
    </div>
  `
  }
}

if (!window.customElements.get('tagalys-search')) {
  window.customElements.define('tagalys-search', TagalysSearch)
}
if (!window.customElements.get('tagalys-search-child-component')) {
  window.customElements.define(
    'tagalys-search-child-component',
    TagalysSearchChildComponent,
  )
}
if (!window.customElements.get('tagalys-results-text')) {
  window.customElements.define('tagalys-results-text', TagalysResultsText)
}
if (!window.customElements.get('tagalys-product-list')) {
  window.customElements.define('tagalys-product-list', TagalysProductList)
}
if (!window.customElements.get('tagalys-pagination')) {
  window.customElements.define('tagalys-pagination', TagalysPagination)
}
if (!window.customElements.get('tagalys-facets')) {
  window.customElements.define('tagalys-facets', TagalysFacets)
}
if (!window.customElements.get('tagalys-sort-select')) {
  window.customElements.define('tagalys-sort-select', TagalysSortSelect)
}
if (!window.customElements.get('tagalys-active-facets')) {
  window.customElements.define('tagalys-active-facets', TagalysActiveFacets)
}
